// =====================================================================
// SEGURIDAD DE TU CUENTA · ESTABILIDAD Parte D (D1–D4)
// =====================================================================
// Vive dentro de «Mi cuenta». Tres bloques, cada uno con su estado vacío:
//   · El segundo factor: si está puesto, cuántos códigos de respaldo
//     quedan, y generar códigos nuevos (pide el código de ahora).
//   · Las llaves de acceso: entrar con la huella o el Face ID.
//   · Los aparatos de confianza: dónde no se pide el código al entrar.
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ShieldCheck, MonitorSmartphone, Loader2 } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import ModalConfirmar from '../ui/ModalConfirmar';
import AsistenteSegundoFactor from './AsistenteSegundoFactor';
import LlavesDeAcceso from './LlavesDeAcceso';
import { limpiarCodigo, codigoCompleto } from './segundoFactor';
import { ContextoAuth } from '../lib/contextoAuth';
// Peaje U-02: hoja de impresión propia y copiar con respaldo.
import HojaDeCodigos from '../peaje/HojaDeCodigos';
import { imprimirHoja } from '../peaje/hojaDeCodigos';
import BotonCopiar from '../peaje/BotonCopiar';

// L-3: el bloque puede llevar `id` (el ancla del enlace) y `tituloRef`
// (para darle el foco al título al llegar por el enlace). Desde que los
// tres viven dentro de «Seguridad», su título es un h3.
function Bloque({ id, icono: Icono, titulo, tituloRef, descripcion, children }) {
  const idTitulo = id ? `${id}-titulo` : undefined;
  return (
    <section id={id} aria-labelledby={idTitulo} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3 scroll-mt-20">
      <div className="flex items-start gap-2">
        <Icono className="h-5 w-5 text-[var(--color-marca)] mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <h3 id={idTitulo} ref={tituloRef} tabIndex={tituloRef ? -1 : undefined}
              className="font-semibold text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-marca)] rounded">{titulo}</h3>
          <p className="text-xs text-gray-500">{descripcion}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function Mensaje({ tipo = 'ok', children }) {
  if (!children) return null;
  const clases = tipo === 'ok' ? 'text-green-800 bg-green-50 border-green-200'
    : 'text-red-700 bg-red-50 border-red-200';
  return <p role={tipo === 'ok' ? 'status' : 'alert'} className={`text-sm border rounded-lg p-2.5 ${clases}`}>{children}</p>;
}

function fecha(valor) {
  if (!valor) return 'nunca';
  try { return new Date(valor).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return valor; }
}

function SegundoFactor() {
  const [estado, setEstado] = useState(null);
  const [configurando, setConfigurando] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [nuevos, setNuevos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);
  const listaNuevos = useRef(null);
  const sesion = useContext(ContextoAuth);

  const cargar = useCallback(() => {
    api.get('/auth/2fa/estado').then(setEstado)
      .catch((e) => setError(e.message || 'No se pudo leer tu segundo factor. Revisa la conexión.'));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function regenerar() {
    setCargando(true);
    setError(null);
    try {
      const r = await api.post('/seguridad/2fa/codigos/regenerar', { codigo: limpiarCodigo(codigo) });
      setNuevos(r.codigos_de_respaldo);
      setCodigo('');
      cargar();
    } catch (e) {
      setError(e.message || 'No se pudieron generar códigos nuevos.');
    } finally {
      setCargando(false);
    }
  }

  if (configurando) {
    return (
      <AsistenteSegundoFactor
        onTerminar={() => { setConfigurando(false); cargar(); }}
        onPosponer={() => setConfigurando(false)}
      />
    );
  }

  return (
    <Bloque icono={ShieldCheck} titulo="Segundo factor"
            descripcion="Una segunda prueba de que eres tú, además de tu contraseña.">
      <Ayuda id="seguridad.segundo_factor.pantalla" />
      {!estado && !error && <p className="text-sm text-gray-500">Cargando…</p>}
      {estado && !estado.activado && (
        <div className="space-y-2">
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            Sin configurar.{estado.le_hace_falta ? ' Tu cuenta lo necesita.' : ''}
          </p>
          <button type="button" onClick={() => setConfigurando(true)}
                  className="min-h-11 rounded-lg bg-[var(--color-marca)] px-4 py-2 text-sm font-semibold text-white">
            Configurar ahora
          </button>
        </div>
      )}
      {estado && estado.activado && (
        <div className="space-y-3">
          <p className="text-sm text-green-800">Activo. Te quedan {estado.codigos_restantes} códigos de respaldo sin usar.</p>
          {nuevos ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">Tus códigos nuevos (los anteriores ya no sirven):</p>
              {/* Peaje U-02: 1 columna en el teléfono, 2 desde 480 px; hoja de impresión propia y copiar con respaldo. */}
              <ul ref={listaNuevos} className="grid grid-cols-1 gap-2 rounded-lg bg-gray-100 p-3 min-[480px]:grid-cols-2">
                {nuevos.map((c) => <li key={c} className="min-w-0 break-all font-mono text-base tracking-wide">{c}</li>)}
              </ul>
              <Ayuda id="seguridad.segundo_factor.codigos_nuevos" />
              <HojaDeCodigos codigos={nuevos} cuenta={sesion?.usuario?.nombre || ''} />
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <button type="button" onClick={imprimirHoja} className="min-h-11 rounded-lg border border-gray-300 px-3 py-2 text-sm">Imprimir</button>
                <BotonCopiar texto={nuevos.join('\n')} objetivo={listaNuevos} etiqueta="Copiar" confirmacion="Copiados" />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label htmlFor="codigo-regenerar" className="block text-sm font-medium">Código de tu aplicación ahora</label>
              <input id="codigo-regenerar" value={codigo} inputMode="text" autoComplete="one-time-code"
                     onChange={(e) => setCodigo(limpiarCodigo(e.target.value))}
                     className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono sm:max-w-xs" placeholder="123456" />
              <button type="button" onClick={regenerar} disabled={!codigoCompleto(codigo) || cargando}
                      className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-60">
                {cargando && <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />}
                Generar códigos nuevos
              </button>
              <Ayuda id="seguridad.segundo_factor.regenerar" />
            </div>
          )}
        </div>
      )}
      <Mensaje tipo="error">{error}</Mensaje>
    </Bloque>
  );
}

function Aparatos() {
  const [datos, setDatos] = useState(null);
  const [todos, setTodos] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(() => {
    api.get('/seguridad/aparatos-de-confianza').then(setDatos)
      .catch((e) => setError(e.message || 'No se pudieron leer tus aparatos. Revisa la conexión.'));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function retirar(id) {
    setError(null);
    try {
      const r = await api.post(`/seguridad/aparatos-de-confianza/${id}/revocar`, {});
      setMsg(r.mensaje);
      cargar();
    } catch (e) {
      setError(e.message || 'No se pudo retirar el aparato.');
    }
  }

  return (
    <Bloque icono={MonitorSmartphone} titulo="Aparatos de confianza"
            descripcion="Donde no se te pide el código al entrar, durante 30 días.">
      {datos && datos.aparatos.length === 0 && <p className="text-sm text-gray-600">{datos.vacio}</p>}
      {datos && datos.aparatos.length > 0 && (
        <>
          <ul className="divide-y divide-gray-100">
            {datos.aparatos.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 py-2">
                <div>
                  <p className="text-sm font-medium">{a.apodo || 'Aparato sin nombre'}</p>
                  <p className="text-xs text-gray-500">Último uso {fecha(a.ultimo_uso_en)} · vence {fecha(a.expira_en)}</p>
                </div>
                <button type="button" onClick={() => retirar(a.id)}
                        className="min-h-11 rounded-lg px-3 text-sm text-red-700 hover:bg-red-50">Retirar</button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => setTodos(true)}
                  className="min-h-11 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700">Retirar todos</button>
          <Ayuda id="seguridad.aparatos.retirar" />
        </>
      )}
      <Mensaje>{msg}</Mensaje>
      <Mensaje tipo="error">{error}</Mensaje>
      {todos && (
        <ModalConfirmar
          titulo="Retirar todos los aparatos de confianza"
          descripcion="En todos ellos se volverá a pedir el código al entrar."
          peligro
          etiquetaConfirmar="Retirar todos"
          onConfirmar={async () => { const r = await api.post('/seguridad/aparatos-de-confianza/revocar-todos', {}); setMsg(r.mensaje); setTodos(false); cargar(); }}
          onCerrar={() => setTodos(false)}
        />
      )}
    </Bloque>
  );
}

// DIRECTRIZ-LLAVE L-3: los tres bloques van bajo un título «Seguridad»,
// que es el nombre con que la pantalla de entrada y el manual los citan
// («Mi cuenta → Seguridad → Llaves de acceso»).
export default function SeguridadDeLaCuenta() {
  return (
    <section aria-labelledby="mi-cuenta-seguridad" className="space-y-3">
      <div>
        <h2 id="mi-cuenta-seguridad" className="text-base font-semibold text-gray-900">Seguridad</h2>
        <p className="text-xs text-gray-500">Cómo demuestras que eres tú al entrar, y desde qué aparatos.</p>
      </div>
      <SegundoFactor />
      <LlavesDeAcceso Contenedor={Bloque} />
      <Aparatos />
    </section>
  );
}
